import React from "react";
import {
  Stack,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Translate as TranslateIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

const isPreviewable = (mimeType: string) =>
  mimeType === "application/pdf" || mimeType.startsWith("image/");

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

interface DocumentFileCardProps {
  doc: any;
  onView: (doc: any) => void;
  onDownload: (doc: any) => void;
  onDelete: (docId: string) => void;
}

export const DocumentFileCard: React.FC<DocumentFileCardProps> = ({
  doc,
  onView,
  onDownload,
  onDelete,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        ml: 5,
        mr: 2,
        mb: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: tokens.radius.sm,
        bgcolor: `${tokens.status.success}06`,
        border: `1px solid ${tokens.status.success}15`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <FileIcon sx={{ fontSize: 14, color: doc.revisionReason ? tokens.status.warning : tokens.text.muted, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: tokens.text.body,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doc.filename}
        </Typography>
        {doc.isEnglish === false && doc.detectedLanguage && (
          <Tooltip title={`Document is in ${doc.detectedLanguage} — English translation may be needed`}>
            <Chip
              icon={<TranslateIcon sx={{ fontSize: "12px !important" }} />}
              label={doc.detectedLanguage}
              size="small"
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: tokens.fontWeight.semibold,
                bgcolor: `${tokens.status.info}12`,
                color: tokens.status.info,
                flexShrink: 0,
                "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
              }}
            />
          </Tooltip>
        )}
        {doc.revisionReason && (
          <Tooltip title={doc.revisionReason}>
            <Chip
              label="Revision"
              size="small"
              sx={{
                height: 16,
                fontSize: 9,
                fontWeight: tokens.fontWeight.semibold,
                bgcolor: `${tokens.status.warning}12`,
                color: tokens.status.warning,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
        {doc.sizeBytes && (
          <Typography
            sx={{
              fontSize: tokens.fontSize["2xs"],
              color: tokens.text.muted,
              flexShrink: 0,
            }}
          >
            {formatFileSize(doc.sizeBytes)}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        {isPreviewable(doc.mimeType) ? (
          <Tooltip title="View Document">
            <IconButton
              size="small"
              onClick={() => onView(doc)}
              sx={{
                color: tokens.text.body,
                "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
              }}
            >
              <ViewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => onDownload(doc)}
              sx={{
                color: tokens.text.body,
                "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
              }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(doc._id)}
            sx={{
              color: tokens.text.muted,
              "&:hover": { color: tokens.status.danger, bgcolor: `${tokens.status.danger}08` },
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
